import React from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';

import { getPreferredName } from '@modules/shared/utils/preferredName';
import {
  DateView,
  PersonAvatar,
  S,
  TableHeadingSort,
} from '@shared/components';
import { intl } from '@shared/localization';
import { tableSortOrder } from '@utils';

import PendingItemAdvancementName from '../PendingItemAdvancementName';
import PendingItemType from '../PendingItemType';
import styles from './PendingItemsTable.less';

const defaultFunct = () => {};

export default ({
  sorter,
  onGoToYouthProfile,
  onGoToAdultProfile,
  onGoToPODetails = false,
  shouldDisableRecord = defaultFunct,
}) => {
  const nameSortOrder = tableSortOrder(sorter, 'name');
  const advancementItemSortOrder = tableSortOrder(sorter, 'advancementItem');
  const completionDateSortOrder = tableSortOrder(sorter, 'completionDate');

  return [
    {
      key: 'avatar',
      width: '5.5rem',
      align: 'right',
      render(_, { profilePictureUrl, isAdult, gender }) {
        return (
          <PersonAvatar
            isAdult={isAdult}
            gender={gender}
            src={profilePictureUrl}
          />
        );
      },
    },
    {
      key: 'name',
      width: '20rem',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: nameSortOrder,
      title: (
        <TableHeadingSort name="name" sorter={sorter}>
          <FormattedMessage id="advancement.PendingItems.column.name" />
        </TableHeadingSort>
      ),
      render(record) {
        const {
          userId,
          isAdult,
          activityTypeId,
          firstName,
          lastName,
          nickName,
        } = record;
        const personPreferredName = getPreferredName({
          firstName,
          lastName,
          nickName,
        });
        const passedFunction = shouldDisableRecord !== defaultFunct;
        const shouldDisable = passedFunction && shouldDisableRecord(record);

        if (shouldDisable) {
          return (
            <S
              size="4"
              bold
              inline
              colored
              title={personPreferredName}
              className={styles.youthName}
              onClick={
                isAdult
                  ? userId && onGoToAdultProfile(userId, activityTypeId)
                  : userId && onGoToYouthProfile(userId, activityTypeId)
              }
            >
              {personPreferredName}
            </S>
          );
        }

        return (
          <S size="4" bold inlineBlock>
            {personPreferredName}
          </S>
        );
      },
    },
    {
      dataIndex: 'memberId',
      title: intl.formatMessage({
        id: 'advancement.PendingItems.column.memberId',
      }),
      width: '10rem',
    },
    {
      key: 'advancementType',
      title: intl.formatMessage({
        id: 'advancement.PendingItems.column.itemType',
      }),
      render(_, item) {
        return <PendingItemType item={item} />;
      },
    },
    {
      key: 'advancementItem',
      title: (
        <TableHeadingSort name="advancementItem" sorter={sorter}>
          <FormattedMessage id="advancement.PendingItems.column.itemDescription" />
        </TableHeadingSort>
      ),
      sorter: (a, b) => {
        if (a.advancementName == null && b.advancementName == null) return 0;
        if (a.advancementName == null) return -1;
        if (b.advancementName == null) return 1;

        return a.advancementName.localeCompare(b.advancementName);
      },
      sortOrder: advancementItemSortOrder,
      render(_, { type, ...item }) {
        return <PendingItemAdvancementName type={type} item={item} showIcon />;
      },
    },
    {
      key: 'completionDate',
      dataIndex: 'date',
      title: (
        <TableHeadingSort name="completionDate" sorter={sorter}>
          <FormattedMessage id="advancement.PendingItems.column.itemCompletionDate" />
        </TableHeadingSort>
      ),
      sorter: (a, b) => {
        const dateA = DateTime.fromISO(a.date);
        const dateB = DateTime.fromISO(b.date);
        return dateA.toMillis() - dateB.toMillis();
      },
      sortOrder: completionDateSortOrder,
      render(date) {
        return <DateView value={date} />;
      },
    },
    ...(onGoToPODetails
      ? [
          {
            key: 'poid',
            title: intl.formatMessage({
              id: 'advancement.PendingItems.column.poid',
            }),
            render({ poid }) {
              return (
                <S
                  size="4"
                  bold
                  inline
                  colored
                  title={poid}
                  className={styles.poid}
                  onClick={() => onGoToPODetails(poid)}
                >
                  {poid}
                </S>
              );
            },
          },
        ]
      : []),
  ];
};
